import type { WorldId } from '../../data/ids';
import type { WorldTheme } from './theme';
import { WORLD_2 } from './cali';
import { WORLD_3 } from './guadalajara';
import { WORLD_4 } from './sinaloa';
import { USA_EL_PASO } from './usa-el-paso';
import { USA_MIAMI } from './usa-miami';
import { USA_NEW_YORK } from './usa-new-york';
import { USA_SAN_DIEGO } from './usa-san-diego';
import { WORLD_1, WORLD_1_DAWN } from './world-1';

/**
 * Registry of the world themes (GDD §14.1, §14.2, issue #68). Each world provides a theme per time of
 * day; the level data picks the variant (dawn, noon, dusk or night). The palettes are game art and the
 * definite values are recorded in GDD §14.2.
 */
const THEMES: Readonly<Record<WorldId, Readonly<Record<string, WorldTheme>>>> = {
  1: WORLD_1,
  2: WORLD_2,
  3: WORLD_3,
  4: WORLD_4,
};

/** The US sky theme of each world (GDD §11.1, §14.2), activated at the border crossing (#97). */
const US_SKY: Readonly<Partial<Record<WorldId, WorldTheme>>> = {
  1: USA_MIAMI,
  2: USA_NEW_YORK,
  3: USA_EL_PASO,
  4: USA_SAN_DIEGO,
};

type TimeOfDay = 'dawn' | 'noon' | 'afternoon' | 'dusk' | 'night';

/**
 * Variant name of a time of day per world (issue #68, #114). Each world names its own variants; world 3
 * calls its morning `manana` instead of the world 1 `amanecer`.
 */
const VARIANT_BY_TIME: Readonly<Record<WorldId, Readonly<Partial<Record<TimeOfDay, string>>>>> = {
  1: { dawn: 'amanecer', noon: 'mediodia', afternoon: 'tarde', dusk: 'atardecer', night: 'noche' },
  2: { afternoon: 'tarde', dusk: 'atardecer', night: 'noche' },
  3: { dawn: 'manana', noon: 'manana', afternoon: 'tarde', dusk: 'atardecer', night: 'atardecer' },
  4: { dawn: 'manana', noon: 'manana', afternoon: 'tarde', dusk: 'tarde', night: 'noche' },
};

/**
 * The theme of the level, chosen by its world and its time of day. Worlds with their own art pick the
 * matching variant (world 1 in #68/#97, world 3 in #114); the rest fall
 * back to the dawn theme of world 1, a game-art placeholder that keeps every level playable.
 */
export function themeFor(world: WorldId, timeOfDay: TimeOfDay | undefined): WorldTheme {
  const variants = THEMES[world];
  const wanted = timeOfDay ? VARIANT_BY_TIME[world][timeOfDay] : undefined;
  const found = (wanted && variants[wanted]) || Object.values(variants)[0];
  return found ?? WORLD_1_DAWN;
}

/** Looks a theme up by id and variant, whatever its world; rejects an unknown one naming it (issue #68). */
export function themeById(id: string, variant?: string): WorldTheme {
  for (const variants of Object.values(THEMES)) {
    for (const theme of Object.values(variants)) {
      if (theme.id === id && (variant === undefined || theme.variant === variant)) return theme;
    }
  }
  for (const theme of Object.values(US_SKY)) {
    if (theme && theme.id === id && (variant === undefined || theme.variant === variant)) return theme;
  }
  throw new Error(`Unknown theme "${id}"${variant ? ` variant "${variant}"` : ''}`);
}

/** The US sky theme of a world, or null if it has none yet (#97). */
export function usSkyThemeFor(world: WorldId): WorldTheme | null {
  return US_SKY[world] ?? null;
}

/** Layers of a theme, for tests that check the depth planes of GDD §14.1. */
export { LAYER_Z } from './theme';
